import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { CatDropdown } from '@blocks-react/components/Dropdown';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import {
  BlocksSelectEvent,
  CatIconWarningTriangle
} from 'components/blocks-components';
import { BlocksChangeEvent } from '@blocks-web-components/events';
import { MyEquipmentList } from 'components/my-equipment/types';
import {
  CREATE_LIST_MODAL_FIELDS,
  DEFAULT_EQUIPMENT_VALUE
} from '../constants/common';
import { equipment } from 'services/savedLists/types';
import { getItemToString } from 'app/[lang]/[esite]/(core)/account/(with-nav)/saved-lists/_components/saved-lists-table/utils';
import styles from '../create-list-modal.module.scss';

const EquipmentDropdown = ({
  selectedEquipmentIndex,
  myEquipmentList,
  isError,
  isListSettings = false,
  equipmentSearch,
  listEquipment,
  setEquipmentSearch
}: {
  selectedEquipmentIndex: number;
  myEquipmentList: MyEquipmentList[];
  isError: boolean;
  isListSettings?: boolean;
  equipmentSearch?: string;
  listEquipment?: equipment | object;
  setEquipmentSearch?: Dispatch<SetStateAction<string>>;
}) => {
  const t = useTranslations();
  const { setValue, register, watch } = useFormContext();
  const { pending } = useFormStatus();
  const hasEquipmentSelected = selectedEquipmentIndex > -1;
  const equipmentText = t('SELECT_EQUIPMENT');
  const selectedEquipment =
    (hasEquipmentSelected && myEquipmentList?.[selectedEquipmentIndex]) ||
    ({} as MyEquipmentList);
  const equipmentDropdownValue = watch(CREATE_LIST_MODAL_FIELDS.EQUIPMENT);
  const onChangeDropdown = (e) => {
    setValue(
      CREATE_LIST_MODAL_FIELDS.EQUIPMENT,
      e?.detail.value || equipmentText
    );
  };

  const onSearchChangeDropdown = (e) => {
    setValue(CREATE_LIST_MODAL_FIELDS.EQUIPMENT, {});
    const filterSearch = e?.detail.value;
    setEquipmentSearch(filterSearch);
  };

  const dropdownList = myEquipmentList?.map((item: MyEquipmentList) => ({
    key: item.uniqueIndex,
    label: getItemToString(item),
    value: {
      serialNumber: item.serialNumber,
      model: item.model,
      assetName: item.assetName
    }
  }));

  const filterDropdownSearchList = useMemo(() => {
    if (equipmentSearch === '') {
      return dropdownList;
    }
    return dropdownList?.filter((item) => {
      const filterList = item.label
        .toLowerCase()
        .includes(equipmentSearch?.toLowerCase());
      return filterList;
    });
  }, [equipmentSearch, dropdownList]);
  const listSettingSelectedEquipment = selectedEquipmentIndex
    ? listEquipment
    : null;

  const updatedEquipment = isListSettings
    ? listSettingSelectedEquipment
    : {
        serialNumber: selectedEquipment?.serialNumber,
        model: selectedEquipment?.model,
        assetName: selectedEquipment?.assetName
      };

  useEffect(() => {
    if (hasEquipmentSelected && !equipmentDropdownValue) {
      setValue(CREATE_LIST_MODAL_FIELDS.EQUIPMENT, updatedEquipment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEquipmentSelected, equipmentDropdownValue, setValue]);

  const getPlaceHolder = () => {
    const listSettingsPlaceholder = selectedEquipmentIndex
      ? getItemToString(listEquipment)
      : equipmentText;
    const createListPlaceholder = hasEquipmentSelected
      ? getItemToString(selectedEquipment)
      : equipmentText;
    return isListSettings ? listSettingsPlaceholder : createListPlaceholder;
  };

  return (
    <>
      <div className="cat-u-padding-bottom-sm">
        <span className="cat-u-theme-typography-label-sm">
          {t('ASSOCIATE_EQUIPMENT')}{' '}
        </span>
        <span
          className={`${styles['font-gray-strong']} cat-u-theme-typography-footnote`}
        >
          {t('OPTIONAL_LABEL_LOW_CASE')}
        </span>
      </div>
      {isError ? (
        <div className="d-flex cat-u-theme-typography-body-sm">
          <CatIconWarningTriangle
            size="sm"
            color="error"
            slot="before"
            data-testid="error-icon"
          />
          <span
            className={`${styles['font-orange-warning']} cat-u-theme-typography-label-sm`}
          >
            &nbsp;{t('FAIL_LOAD_EQUIPMENT_LIST')}
          </span>
        </div>
      ) : (
        <>
          {dropdownList?.length && (
            /* TODO - Input Validation - Ensure that <,>,/,\ characters and <script> tags are not allowed to type/paste. */
            <CatDropdown
              {...register(CREATE_LIST_MODAL_FIELDS.EQUIPMENT)}
              onBlChange={(e: BlocksChangeEvent) => onSearchChangeDropdown(e)}
              onBlSelect={(e: BlocksSelectEvent) => onChangeDropdown(e)}
              placeholder={getPlaceHolder()}
              fieldNote={t('SEARCH_EQUIP_SN_MODEL_ASSET')}
              size="md"
              hideLabel
              isTypeahead
              disabled={pending}
            >
              <CatList>
                {filterDropdownSearchList.length ? (
                  <>
                    <CatListItem value={DEFAULT_EQUIPMENT_VALUE}>
                      {equipmentText}
                    </CatListItem>
                    {filterDropdownSearchList?.map((item) => {
                      return (
                        <CatListItem
                          key={item.key}
                          value={item}
                          isActive={selectedEquipment?.uniqueIndex === item.key}
                          isCheckIcon={
                            selectedEquipment?.uniqueIndex === item.key
                          }
                          className="text-truncate"
                        />
                      );
                    })}
                  </>
                ) : (
                  <CatListItem isStatic>{t('NO_RESULTS_FOUND')}</CatListItem>
                )}
              </CatList>
            </CatDropdown>
          )}
          {!hasEquipmentSelected && !dropdownList?.length && (
            <div className="cat-u-theme-typography-footnote">
              {t('NO_EQUIPMENT_ASSOCIATED_ACCOUNT')}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default EquipmentDropdown;
